import { z } from "zod";
export const productDeliveryInputSchema = z.object({
  productId: z.string().min(1).max(100),
  commune: z.string().trim().min(1).max(100),
});
export const productDeliveryResponseSchema = z.object({
  estimate: z
    .object({
      amount: z.number().finite().nonnegative(),
      commune: z.string(),
      simulated: z.boolean(),
    })
    .nullable(),
});
export const productBookingSelectionSchema = z
  .object({
    product_id: z.string().min(1),
    variant_id: z.string().min(1),
    region_id: z.string().min(1),
    country_code: z.literal("cl"),
    service_id: z.string().min(1),
    slot_id: z.string().min(1),
    address_1: z.string().trim().min(3).max(200),
    address_2: z.string().trim().max(200),
    commune: z.string().trim().min(1).max(100),
  })
  .strict();
export type ProductBookingSelection = z.infer<
  typeof productBookingSelectionSchema
>;
export const bookingCartSchema = z.object({
  id: z.string(),
  items: z.array(
    z.object({
      id: z.string(),
      variant_id: z.string(),
      product_id: z.string(),
      quantity: z.coerce.number(),
    })
  ),
});
export const bookingProductSchema = z.object({
  product: z.object({
    id: z.string(),
    variants: z.array(z.object({ id: z.string() })),
  }),
});
export function createWebpayRedirectSchema(localSimulation = false) {
  return z.object({
    token: z.string().min(1).max(512),
    url: z.url().refine((value) => {
      const u = new URL(value);
      return (
        !u.username &&
        !u.password &&
        ((u.protocol === "https:" && u.hostname.endsWith(".transbank.cl")) ||
          (localSimulation &&
            u.origin === "http://localhost:19139" &&
            u.pathname === "/portal"))
      );
    }, "Destino Webpay inválido"),
  });
}
export const webpayRedirectSchema = createWebpayRedirectSchema();
export const productInstallationSchema = z.object({
  communes: z.array(z.string()),
  services: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      instructions: z.string(),
      covered: z.boolean(),
      price: z.number().nullable(),
      price_includes_tax: z.boolean(),
      duration_minutes: z.number(),
      slots: z.array(
        z.object({
          id: z.string(),
          start_at: z.iso.datetime({ offset: true }),
          end_at: z.iso.datetime({ offset: true }),
        })
      ),
    })
  ),
});
export const installationOptionsSchema = z.object({
  services: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      service_variant_id: z.string(),
      product_line_ids: z.array(z.string()),
      instructions: z.string(),
      price: z.number().nullable(),
      price_includes_tax: z.boolean(),
      covered: z.boolean(),
      slots: z.array(
        z.object({ id: z.string(), start_at: z.string(), end_at: z.string() })
      ),
    })
  ),
  appointment: z
    .object({
      id: z.string(),
      service_id: z.string(),
      slot_id: z.string(),
      status: z.string(),
      expires_at: z.string(),
      service_line_id: z.string(),
      product_line_id: z.string(),
    })
    .nullable(),
});
export type InstallationOptions = z.infer<typeof installationOptionsSchema>;
export type ProductInstallationOptions = z.infer<
  typeof productInstallationSchema
>;
export const orderInstallationSchema = z.object({
  appointment: z
    .object({
      status: z.string(),
      service_name: z.string(),
      instructions: z.string(),
      start_at: z.string(),
      end_at: z.string(),
    })
    .nullable(),
});
export const installationSelectionSchema = z
  .object({
    service_id: z.string().min(1),
    slot_id: z.string().min(1),
    product_line_id: z.string().min(1),
  })
  .strict();
export const orderAftercareSchema = z.object({
  delivery_status: z.enum(["pending", "preparing", "shipped", "delivered"]),
  documents: z.array(
    z.object({
      id: z.string(),
      label: z.string(),
      simulated: z.boolean(),
      type: z.string(),
      total: z.number(),
      net: z.number(),
      tax: z.number(),
      date: z.string(),
      status: z.string(),
      commercial_terms: z
        .object({
          payment_condition: z.enum(["cash", "credit"]),
          due_date: z.string().optional(),
          financed_amount: z.number().optional(),
          interest: z.number().optional(),
          fees: z.number().optional(),
          terms_version: z.string().optional(),
        })
        .optional(),
    })
  ),
});
